import { HrEmployee } from '../types/hr'

/** Integer ngwee helpers — all payroll arithmetic avoids float drift. */
export const toNgwee = (kwacha: number | undefined | null) =>
  Math.round((Number(kwacha) || 0) * 100)
export const toKwacha = (ngwee: number) => Math.round(ngwee) / 100
export const sumKwacha = (values: Array<number | undefined>) =>
  toKwacha(values.reduce<number>((sum, value) => sum + toNgwee(value), 0))

export const formatKwacha = (value: number | undefined | null) =>
  `K ${(Number(value) || 0).toLocaleString('en-ZM', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

export const formatAmount = (value: number | undefined | null) =>
  (Number(value) || 0).toLocaleString('en-ZM', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
export const monthNames = MONTHS

export const currentPeriod = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

export const formatPeriod = (period: string) => {
  const [year, month] = period.split('-').map(Number)
  return `${MONTHS[(month || 1) - 1]} ${year}`
}

export const periodBounds = (period: string) => {
  const [year, month] = period.split('-').map(Number)
  const start = `${period}-01`
  const lastDay = new Date(year, month, 0).getDate()
  return { start, end: `${period}-${String(lastDay).padStart(2, '0')}`, lastDay }
}

export const formatDate = (value?: string) =>
  value
    ? new Date(value.length === 10 ? `${value}T00:00:00` : value).toLocaleDateString(
        'en-GB',
        { day: '2-digit', month: 'short', year: 'numeric' },
      )
    : '—'

export const formatDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

export const todayIso = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

export const maskAccount = (value?: string) => {
  if (!value) return 'Not provided'
  const clean = value.replace(/\s/g, '')
  return clean.length <= 4 ? clean : `•••• ${clean.slice(-4)}`
}

export const employeeName = (employee?: Pick<HrEmployee, 'firstName' | 'middleName' | 'surname'>) =>
  employee
    ? [employee.firstName, employee.middleName, employee.surname]
        .filter(Boolean)
        .join(' ')
    : 'Unknown employee'

/** Weekdays (Mon–Fri) between two ISO dates, inclusive. */
export const countWeekdays = (start: string, end: string) => {
  const from = new Date(`${start}T00:00:00`)
  const to = new Date(`${end}T00:00:00`)
  let days = 0
  for (const day = new Date(from); day <= to; day.setDate(day.getDate() + 1)) {
    const dow = day.getDay()
    if (dow !== 0 && dow !== 6) days += 1
  }
  return days
}

/** Weekday overlap of [start,end] with [rangeStart,rangeEnd]. */
export const overlapWeekdays = (
  start: string,
  end: string,
  rangeStart: string,
  rangeEnd: string,
) => {
  const from = start > rangeStart ? start : rangeStart
  const to = end < rangeEnd ? end : rangeEnd
  return from > to ? 0 : countWeekdays(from, to)
}

export const makeId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
